<template>
  <div>
    <div class="modal modal-blur fade" :class="{ show: !!user }" :style="{ display: user ? 'block' : 'none' }" tabindex="-1">
      <div class="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Edit roles &mdash; {{ user?.email }}</h5>
            <button type="button" class="btn-close" @click="$emit('close')"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label visually-hidden" for="role-filter-input">Filter roles</label>
              <input id="role-filter-input" v-model="filter" type="search" class="form-control" placeholder="Filter roles" autocomplete="off" />
            </div>

            <div v-for="group of visibleGroups" :key="group.name" class="mb-2">
              <button class="btn btn-ghost-secondary w-100 d-flex align-items-center justify-content-between px-2 py-1" @click="toggleGroup(group.name)">
                <span class="text-uppercase small fw-bold">
                  {{ group.name }} <span class="text-secondary">&middot; {{ group.roles.length }}</span>
                </span>
                <span class="text-secondary">{{ isExpanded(group) ? '&#9662;' : '&#9656;' }}</span>
              </button>
              <div v-if="isExpanded(group)" class="mt-1">
                <label v-for="role of group.roles" :key="role.id" class="d-flex align-items-start py-1 px-2 role-option">
                  <input class="form-check-input m-0 me-2 mt-1" type="checkbox" :checked="selectedIds.includes(role.id)" @change="toggle(role)" />
                  <span>
                    <span :class="isPrivileged(role) ? 'text-danger fw-bold' : ''">{{ role.role }}</span>
                    <span v-if="isPrivileged(role)" class="d-block text-secondary small">Full system access, including every /admin route.</span>
                  </span>
                </label>
              </div>
            </div>

            <div v-if="!visibleGroups.length" class="text-secondary text-center py-3">No roles match "{{ filter }}".</div>
          </div>
          <div class="modal-footer justify-content-between">
            <div class="text-secondary small">
              <span v-if="!pendingCount">No changes</span>
              <span v-else>
                <span v-if="added.length" class="text-success">+ {{ added.map(r => r.role).join(', ') }}</span>
                <span v-if="added.length && removed.length">, </span>
                <span v-if="removed.length" class="text-danger">&minus; {{ removed.map(r => r.role).join(', ') }}</span>
              </span>
            </div>
            <div>
              <button type="button" class="btn me-2" @click="$emit('close')">Cancel</button>
              <button type="button" class="btn btn-primary" :disabled="!pendingCount || saving" @click="save">
                <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
                Save<span v-if="pendingCount"> ({{ pendingCount }})</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade show" v-if="user"></div>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, ref, watch } from 'vue'
import { useStore } from 'vuex'

const props = defineProps({
  user: { type: Object, default: null },
  roles: { type: Array, default: () => [] }
})
const emit = defineEmits(['close', 'saved'])

const $vue = getCurrentInstance()
const store = useStore()

const CORE_ROLES = ['USER', 'SUPER_ADMIN', 'INIT.SKAARHOJ.COM']
const PRIVILEGED_ROLES = ['SUPER_ADMIN']

const filter = ref('')
const saving = ref(false)
const selectedIds = ref([])
const collapsedGroups = ref([])

watch(() => props.user, (user) => {
  filter.value = ''
  collapsedGroups.value = []
  selectedIds.value = (user?.roles || []).map(role => role.id)
})

function isPrivileged (role) {
  return PRIVILEGED_ROLES.includes(role.role)
}

function groupOf (role) {
  if (CORE_ROLES.includes(role.role)) return 'Core'
  if (role.role.startsWith('INIT_')) return 'INIT'
  if (role.role.startsWith('BOMALIZER_')) return 'BOMALIZER'
  return 'Other'
}

const visibleGroups = computed(() => {
  const term = filter.value.trim().toLowerCase()
  const matching = props.roles.filter(role => !term || role.role.toLowerCase().includes(term))
  return ['Core', 'INIT', 'BOMALIZER', 'Other']
    .map(name => ({ name, roles: matching.filter(role => groupOf(role) === name) }))
    .filter(group => group.roles.length)
})

function isExpanded (group) {
  return filter.value.trim() !== '' || !collapsedGroups.value.includes(group.name)
}

function toggleGroup (name) {
  collapsedGroups.value = collapsedGroups.value.includes(name)
    ? collapsedGroups.value.filter(item => item !== name)
    : [...collapsedGroups.value, name]
}

function toggle (role) {
  selectedIds.value = selectedIds.value.includes(role.id)
    ? selectedIds.value.filter(id => id !== role.id)
    : [...selectedIds.value, role.id]
}

const added = computed(() => {
  const originalIds = (props.user?.roles || []).map(role => role.id)
  return props.roles.filter(role => selectedIds.value.includes(role.id) && !originalIds.includes(role.id))
})
const removed = computed(() =>
  (props.user?.roles || []).filter(role => !selectedIds.value.includes(role.id))
)
const pendingCount = computed(() => added.value.length + removed.value.length)

async function save () {
  const grantsPrivilege = added.value.some(isPrivileged)
  if (grantsPrivilege) {
    const res = await $vue?.proxy.$swal.fire({
      icon: 'warning',
      title: 'Grant SUPER_ADMIN?',
      text: 'This grants full system access, including every /admin route. Continue?',
      showCancelButton: true,
      confirmButtonText: 'Yes, grant it',
      cancelButtonText: 'No, cancel'
    })
    if (!res.isConfirmed) return
  }

  saving.value = true
  const attempted = added.value.length + removed.value.length
  const failures = []
  for (const role of added.value) {
    try {
      await store.dispatch('setRole', { Email: props.user.email, Role: role.role })
    } catch (err) {
      failures.push('grant ' + role.role + ': ' + (err?.response?.data?.Data || 'failed'))
    }
  }
  for (const role of removed.value) {
    try {
      await store.dispatch('removeRole', { Email: props.user.email, Role: role.role })
    } catch (err) {
      failures.push('revoke ' + role.role + ': ' + (err?.response?.data?.Data || 'failed'))
    }
  }
  saving.value = false

  if (failures.length) {
    $vue?.proxy.$swal.fire({
      icon: 'error',
      title: failures.length === attempted ? 'No changes were applied' : 'Some changes failed',
      html: failures.join('<br>')
    })
    // Nothing landed, so stay open and keep the selection the user made.
    if (failures.length === attempted) return
  }
  emit('saved')
}
</script>

<style scoped>
.role-option {
  cursor: pointer;
  border-radius: var(--tblr-border-radius);
}
.role-option:hover {
  background: var(--tblr-active-bg, rgba(255, 255, 255, 0.04));
}
</style>
